import type {
  AdminEmailDto,
  EmailStatusDto,
  KindleAddressDto,
  KindleAvailabilityDto,
  KindleDeliveryDto,
  KindleDeliveryListDto,
} from "@shelflist/shared";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { api, call } from "./api";
import { featuresQuery } from "./metadata";

export const kindleKeys = {
  emailStatus: ["email", "status"] as const,
  adminEmail: ["admin", "email"] as const,
  address: ["me", "kindle"] as const,
  deliveries: ["me", "kindle", "deliveries"] as const,
  delivery: (id: string) => ["me", "kindle", "deliveries", "one", id] as const,
  availability: (bookId: string) => ["books", "kindle", bookId] as const,
};

export const emailStatusQuery = queryOptions({
  queryKey: kindleKeys.emailStatus,
  queryFn: (): Promise<EmailStatusDto> => call(api.email.status.$get()),
  staleTime: 60_000,
});

export const adminEmailQuery = queryOptions({
  queryKey: kindleKeys.adminEmail,
  queryFn: (): Promise<AdminEmailDto> => call(api.admin.email.$get()),
});

export const kindleAddressQuery = queryOptions({
  queryKey: kindleKeys.address,
  queryFn: (): Promise<KindleAddressDto> => call(api.me.kindle.$get()),
});

export const kindleAvailabilityQuery = (bookId: string) =>
  queryOptions({
    queryKey: kindleKeys.availability(bookId),
    queryFn: (): Promise<KindleAvailabilityDto> =>
      call(api.books[":id"].kindle.$get({ param: { id: bookId } })),
  });

export const kindleDeliveriesQuery = (page: number) =>
  queryOptions({
    queryKey: [...kindleKeys.deliveries, "page", page] as const,
    queryFn: (): Promise<KindleDeliveryListDto> =>
      call(api.me.kindle.deliveries.$get({ query: { page: String(page) } })),
  });

/** One delivery; polled every 2 s while it is still `pending`. */
export const kindleDeliveryQuery = (id: string) =>
  queryOptions({
    queryKey: kindleKeys.delivery(id),
    queryFn: (): Promise<KindleDeliveryDto> =>
      call(api.me.kindle.deliveries[":id"].$get({ param: { id } })),
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2000 : false),
  });

/** True when outgoing email is configured (Send to Kindle is then available). */
export function useEmailConfigured(): boolean {
  return useQuery(featuresQuery).data?.email.configured ?? false;
}
